import React, { useCallback, useEffect, useRef } from 'react';
import { Action, History, Location } from 'history';
import { BehaviorSubject, noop, Observable, Subject } from 'rxjs';
import { scan, share } from 'rxjs';

import { createDebug } from '../utils/runtimeDebug';

const debug = createDebug('AsyncRouter');

interface AsyncContext {
    pathname: string | null;
    ready();
    complete(v: any);
    registerBase(path: string);
    base$: React.MutableRefObject<BehaviorSubject<Set<string>>> | null;
}
export const AsyncRouterContext = React.createContext<AsyncContext>({
    pathname: null,
    ready: noop,
    complete: (_v) => {},
    registerBase: (_v) => {},
    base$: null,
});

interface AsyncRouterProps {
    history: History;
    complete$: Subject<any>;
}

export const AsyncRouter: React.FC<React.PropsWithChildren<AsyncRouterProps>> = (props) => {
    const base$ = useRef(new BehaviorSubject(new Set<string>([])));
    const input$ = useRef(new Subject<string>());
    const complete = useCallback(
        (v) => {
            props.complete$.next(v);
        },
        [props.complete$],
    );
    const registerBase = useCallback(
        (v) => {
            debug('incoming input$');
            input$.current.next(v);
        },
        [input$],
    );

    useEffect(() => {
        const sub = input$.current
            .pipe(
                scan((acc, item) => {
                    acc.add(item);
                    return acc;
                }, new Set<string>([])),
                share(),
            )
            .subscribe(base$.current);
        return () => sub.unsubscribe();
    }, [input$]);

    return (
        <AsyncRouterContext.Provider
            value={{
                pathname: '/',
                ready: () => {},
                complete,
                registerBase,
                base$,
            }}
        >
            {props.children}
        </AsyncRouterContext.Provider>
    );
};

export interface HistoryEvent {
    location: Location;
    action: Action;
}

/**
 * Wrap events from the history obj as an observable
 *
 * @param history
 */
export function historyAsObs(history: History): Observable<HistoryEvent> {
    return new Observable((obs) => {
        debug('history listener added');
        /**
         * history v5 (required by react-router 6/7) changed the `listen` callback
         * signature from `(location, action)` to `(update)` where
         * `update = { action, location }`. The old dual-argument handler used to
         * receive `location = <update-object>` and `action = undefined`, which
         * meant every SPA navigation emitted `{ location: {action, location}, action: undefined }`
         * downstream. `AsyncLoader` then called `resolveLocal(next.location.pathname)`
         * with `pathname = undefined` and the route never resolved, so clicking any
         * `<Link>` produced a blank page even though the server rendered fine
         * when the same URL was hit directly.
         */
        const handler = (update: { location: Location; action: Action }) => {
            obs.next({
                location: update.location,
                action: update.action,
            });
        };
        const unlisten = history.listen(handler);
        return () => {
            if (typeof unlisten === 'function') {
                debug('history listener teardown');
                unlisten();
            } else {
                console.error('Could not tear down the history component');
            }
        };
    });
}
